import {
  ActionType,
  getResourceLifecycleStatus,
  Listing,
  ListingColumn,
  ListingDisplayNameLink,
  SortDirections,
  stateT,
  Status,
  useNavigation,
} from "o4a-react";
import * as React from "react";
import apiClients, { MultiCloudDatabaseApiVersion } from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { AzureResourceGroupLink } from "../../components/AzureLinks/AzureResourceGroupLink";
import { ConsoleContext } from "../../console/ConsoleContext";
import { Settings, SettingsContext } from "../../console/SettingsContext";
import {
  DetailsPanelId,
  EXADB_CREATE_ROUTE,
  PageId,
  PageRegistrationConfig,
  RESOURCE_ROUTE,
} from "../../constants/pluginConstants";
import { ListingTestIds, MonochromeIconIds, ttlOneMinCaching } from "../../constants/uiConstants";
import { DatabaseSummary, DatabaseSummaryCollection } from "../../gen/clients/mchub-azure-api-client-exa";
import { parseId } from "../../helpers/idHelper";
import { getStatusInfo, optimizedRetryOption, responseItemstoArray } from "../../helpers/resourceHelper";
import { NavigationAnalyticsData, useAnalytics } from "../../hooks/useAnalytics";
import { useQueryCall } from "../../hooks/useQueryCall";
import { useRowCount } from "../../hooks/useRowCount";
import { getAzureLocationName, getOciRegion } from "../../utils";

/**
 * TODO
 * This interface is implement to include the last status and details.
 * Api has not expose these two fields for now. When it will, we might remove this interface.
 */
interface DatabaseSummaryExt extends DatabaseSummary {
  lastOperationStatus?: string;
  lastOperationStatusDetails?: string;
}

const detailsPageRegistrationIds = PageRegistrationConfig[PageId.EXADB_CDB_DETAILS].map(config => config.key);

export interface ExaVmClusterDatabasesListProps {
  vmClusterId: string | undefined;
  location: string;
}

export enum ColumnIds {
  Name = "name",
  ResourceGroupName = "id",
  Location = "location",
  ResourceStatus = "status"
}

export enum ActionIds {
  Create = "create",
  Refresh = "refresh",
}

export enum ActionBarTestIds {
  Create = "create",
  Refresh = "refresh",
}

export const ExaVmClusterDatabasesList = ({ vmClusterId, location }: ExaVmClusterDatabasesListProps): JSX.Element => {
  const { trackActionClick } = useAnalytics();

  const { navigateTo } = useNavigation(ConsoleContext);

  const idComps = parseId(vmClusterId);
  const resGroup = decodeURIComponent(idComps?.resourceGroup || "");

  const { locale } = React.useContext<Settings>(SettingsContext);
  const { subDetailsListingItemCount } = useRowCount();

  const { response, loading, refresh } = useQueryCall({
    wait: !vmClusterId,
    method: apiClients.withRegion(getOciRegion(location)).exaDatabaseApi.listDatabases,
    options: {
      args: {
        vmClusterId,
        subscriptionId: idComps?.subscriptionId || "",
        resourceGroupName: resGroup,
        apiVersion: MultiCloudDatabaseApiVersion,
      },
      caching: ttlOneMinCaching,
      fetchAllPages: true,
      retry: optimizedRetryOption,
    },
    notification: {
      failure: {
        title: Messages.notifications.failure.titles.load(),
        message: Messages.notifications.failure.messages.loadDatabases(),
      },
    },
  });

  const dbItems = response
    && responseItemstoArray<DatabaseSummaryCollection, DatabaseSummary>(response);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const items = React.useMemo(() => dbItems, [JSON.stringify(dbItems)]);

  const getLocation = (value: DatabaseSummary): JSX.Element => (
    <div>{getAzureLocationName(value?.location || "")}</div>
  );

  const getStatus = (value: DatabaseSummaryExt): JSX.Element => (
    <Status
      label={stateT(value?.status || "")}
      tooltip={stateT(value?.status || "")}
      status={getResourceLifecycleStatus(value?.status || "")}
      statusInfo={
        getStatusInfo(
          value?.status || "",
          value?.lastOperationStatus || "",
          value?.lastOperationStatusDetails || "",
          value?.lifecycleDetails,
        )
      }
    />
  );

  const columns: ListingColumn[] = [
    {
      name: Messages.common.name(),
      itemProp: ColumnIds.Name,
      flexGrow: 1,
      isResizable: true,
      initialSortDirection: SortDirections.ASC,
      // eslint-disable-next-line react/no-unstable-nested-components
      onRenderItems: value => (
        <ListingDisplayNameLink
          displayName={value.name}
          navigation={{
            // eslint-disable-next-line max-len
            to: `${RESOURCE_ROUTE}/${value.id}/${PageRegistrationConfig[PageId.EXADB_CDB_DETAILS][0].panelPath}?location=${value.location}`,
            pageKey: detailsPageRegistrationIds[0],
          }}
        />
      ),
    },
    {
      name: Messages.labels.resourceGroup(),
      itemProp: ColumnIds.ResourceGroupName,
      flexGrow: 1,
      isResizable: true,
      initialSortDirection: SortDirections.ASC,
      // eslint-disable-next-line react/no-unstable-nested-components
      onRenderItems: value => <AzureResourceGroupLink resourceId={value.id} />,
    },
    {
      name: Messages.labels.location(),
      itemProp: ColumnIds.Location,
      flexGrow: 1,
      isResizable: true,
      initialSortDirection: SortDirections.ASC,
      onRenderItems: value => getLocation(value),
    },
    {
      name: Messages.labels.status(),
      itemProp: ColumnIds.ResourceStatus,
      flexGrow: 1,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      onRenderItems: value => getStatus(value),
    },
  ];

  const dbActions: ActionType[] = [
    {
      key: ActionIds.Create,
      testId: ActionBarTestIds.Create,
      text: Messages.actions.create(),
      icon: MonochromeIconIds.Create,
      // eslint-disable-next-line max-len
      onClick: () => {
        trackActionClick(ActionIds.Create, PageId.VMCLUSTER_DETAILS, DetailsPanelId.CONTAINERDATABASES);
        navigateTo(EXADB_CREATE_ROUTE, PageId.EXADB_CREATE, {
          vmClusterId,
          location,
          analytics: {
            pageId: PageId.VMCLUSTER_DETAILS,
            panelId: DetailsPanelId.CONTAINERDATABASES,
            actionName: ActionIds.Create,
          } as NavigationAnalyticsData,
        });
      },
    },
    {
      key: ActionIds.Refresh,
      testId: ActionBarTestIds.Refresh,
      text: Messages.actions.refresh(),
      icon: MonochromeIconIds.Refresh,
      onClick: () => {
        trackActionClick(ActionIds.Refresh, PageId.VMCLUSTER_DETAILS, DetailsPanelId.CONTAINERDATABASES);
        refresh();
      },
    },
  ];

  return (
    <Listing
      isLoading={loading}
      testId={ListingTestIds.ExaVmClusterDatabases}
      itemKey={ColumnIds.Name}
      items={items || []}
      emptyList={{ title: Messages.common.noResults() }}
      listColumns={columns}
      itemsPerPage={subDetailsListingItemCount}
      actionBarItems={dbActions}
      sorting={{
        locale,
        initialSortedColumn: ColumnIds.Name,
      }}
    />
  );
};
